import React, { useEffect, useMemo } from 'react';
import { elements } from '@ecosystem/newron-design-system';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { transformPeriodOptions } from '@utils/PeriodUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { Option } from '@utils/pageConfig/Types';
import { getUnavailablePGsByPeriod } from '@utils/productGroupUtils/ProductGroupUtils';
import WithRadio from '@components/design/WithRadio';

export type FocusPeriodProps = {
  periodOptions: BaseTimePeriod[];
  selectedPeriod: BaseTimePeriod;
  selectPeriodCallback: (period: BaseTimePeriod) => void;
  isPeriodOptionsDisabled: boolean;
  selectedPeriodicity: Periodicity;
  appliedCountries?: Option[];
  appliedProductGroups?: Option[];
};

const FocusPeriod: React.FC<FocusPeriodProps> = ({
  periodOptions,
  selectedPeriod,
  selectPeriodCallback,
  isPeriodOptionsDisabled,
  selectedPeriodicity,
  appliedCountries,
  appliedProductGroups,
}) => {
  const pagesConfig = usePageConfig();
  const { Icon } = elements;
  const { Fractional } = Icon.icons;
  const isDisplay = true;
  // Memoized unavailable PGs calculation to avoid repeated calculations
  const unavailablePGsMap = useMemo(() => {
    const map = new Map<string, number>();
    if (isDisplay) {
      periodOptions.forEach((option) => {
        const unavailableCount = getUnavailablePGsByPeriod(
          option.value,
          selectedPeriodicity,
          pagesConfig.pgsByCountry,
          pagesConfig.masterPgsByCountry,
          appliedCountries,
          appliedProductGroups,
        ).length;
        map.set(option.label, unavailableCount);
      });
    }
    return map;
  }, [periodOptions, selectedPeriodicity, isDisplay, appliedCountries, appliedProductGroups, pagesConfig]);

  const transformedPeriodOptions = transformPeriodOptions(periodOptions, selectedPeriodicity);

  // Effect to update the availability state
  useEffect(() => {
    if (isDisplay) {
      const allPgsAvailable = [...unavailablePGsMap.values()].every((count) => count === 0);
      pagesConfig.setIsAllPgsByPeriodicityAvailable(allPgsAvailable);
    }
  }, [unavailablePGsMap, isDisplay, pagesConfig]);

  return (
    <div className="focusPeriodCotainer flex justify-between mb-4">
      <div className="radioButtons">
        <WithRadio
          title="Focus Period"
          options={transformedPeriodOptions}
          onChange={(value) => {
            const period = periodOptions.find((item) => item.value === value);
            selectPeriodCallback(period);
          }}
          value={selectedPeriod.value}
          orientation="vertical"
          isDisabled={isPeriodOptionsDisabled}
        />
      </div>
      {/* Render icons next to each radio button based on the condition */}
      <div className="fractionalIconContainer flex flex-column mt-6">
        {periodOptions.map((option) => {
          let isSomePgsUnavailable = unavailablePGsMap.get(option.label) ?? 0;
          let iconHeight = 'h-8';

          if (option.label.length > 20) {
            iconHeight = 'h-13';
          }

          return (
            <div key={option.value} className={`${iconHeight} flex pl-5 pt-2 items-start`}>
              {isDisplay && isSomePgsUnavailable > 0 && (
                <Icon color="secondaryInteractive">
                  <Fractional />
                </Icon>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FocusPeriod;
